// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import "@ant-design/v5-patch-for-react-19";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import AntdApp from "antd/es/app/App";
import theme from "antd/es/theme";
import { BAIConfigProvider } from "backend.ai-ui";
import enUS from "backend.ai-ui/dist/locale/en_US";
import jaJP from "backend.ai-ui/dist/locale/ja_JP";
import koKR from "backend.ai-ui/dist/locale/ko_KR";
import thTH from "backend.ai-ui/dist/locale/th_TH";
import viVN from "backend.ai-ui/dist/locale/vi_VN";
// https://ant.design/docs/react/replace-moment#Custom-component
// https://github.com/ant-design/ant-design/issues/26190#issuecomment-703673400
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/ja";
import "dayjs/locale/ko";
import "dayjs/locale/th";
import "dayjs/locale/vi";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import updateLocale from "dayjs/plugin/updateLocale";
import utc from "dayjs/plugin/utc";
import weekday from "dayjs/plugin/weekday";
import i18n from "i18next";
import I18NextHttpBackend, {
  type HttpBackendOptions,
} from "i18next-http-backend";
import React, { Suspense, useEffect, useState } from "react";
import {
  I18nextProvider,
  initReactI18next,
  useTranslation,
} from "react-i18next";
import { RelayEnvironmentProvider } from "react-relay/hooks";
import { Navigate, Route, Routes } from "react-router-dom";

import "./App.css";
import { createRelayEnvironment } from "./RelayEnvironment";
// Static (not lazy): it's the Suspense fallback below, so it must be available
// synchronously — a lazy fallback would itself suspend and blank the screen.
import FlexActivityIndicator from "./components/FlexActivityIndicator";
import RedirectIfAuth from "./components/contexts/RedirectIfAuth";
import RequireFeature from "./components/contexts/RequireFeature";
import RequirePrivilege from "./components/contexts/RequirePrivilege";
import "./fonts.css";
import { useColorScheme, usePreference } from "./hooks/atoms/preferences";
import { AuthProvider } from "./hooks/auth";
import CurrentUserErrorToaster from "./infra/CurrentUserErrorToaster";
import QueryErrorHandler from "./infra/QueryErrorHandler";
import { logApiError } from "./infra/apiError";

type Locale = typeof enUS;

const MainLayout = React.lazy(() => import("./components/MainLayout"));

const OutdatedBrowserAlert = React.lazy(
  () => import("./components/OutdatedBrowserAlert")
);

const AccessDenied = React.lazy(() => import("./components/AccessDenied"));
const RequireAuth = React.lazy(
  () => import("./components/contexts/RequireAuth")
);

const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const NoMatch = React.lazy(() => import("./pages/NoMatch"));

const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const PipelineJobDetail = React.lazy(() => import("./pages/PipelineJobDetail"));
const PipelineJobsPage = React.lazy(() => import("./pages/PipelineJobsPage"));
const PipelineList = React.lazy(() => import("./pages/PipelineList"));
const PromptsPage = React.lazy(() => import("./pages/PromptsPage"));
const AgentsPage = React.lazy(() => import("./pages/AgentsPage"));
const AgentDetailPage = React.lazy(() => import("./pages/AgentDetailPage"));
const AgentChatPage = React.lazy(() => import("./pages/AgentChatPage"));
const ModelDeploymentsPage = React.lazy(
  () => import("./pages/ModelDeploymentsPage")
);
const ExperimentsPage = React.lazy(() => import("./pages/ExperimentsPage"));
const ExperimentDetailPage = React.lazy(
  () => import("./pages/ExperimentDetailPage")
);
const PipelineTemplatesPage = React.lazy(
  () => import("./pages/PipelineTemplatesPage")
);
const DataFoldersPage = React.lazy(() => import("./pages/DataFoldersPage"));
const SecretsPage = React.lazy(() => import("./pages/SecretsPage"));
const AuditLogsPage = React.lazy(() => import("./pages/AuditLogsPage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));

dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.extend(timezone);

// TODO: for the reset relay if needed, move this to a separate component with all the providers.
// const myEnvironmentKey = new EnvironmentKey("Backend.AI Environment");

i18n
  .use(initReactI18next)
  .use(I18NextHttpBackend)
  .init<HttpBackendOptions>({
    backend: {
      loadPath: `/static/locales/{{lng}}/{{ns}}.json?v=${process.env.__VITE_BUILD_HASH__}`,
    },
    fallbackLng: "en",
    ns: ["common"], // pre-load common namespace
    interpolation: {
      escapeValue: false,
    },
    react: {
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ["br", "strong", "span", "code", "p"],
    },
  });

const useLocale = (): { locale: Locale } => {
  const { language: locale } = usePreference();
  const { i18n } = useTranslation();

  useEffect(() => {
    i18n
      .changeLanguage(locale)
      .then(() => dayjs.locale(locale))
      .catch((error) => {
        logApiError("useLocale.changeLanguage", error);
        dayjs.locale("en");
      });
  }, [locale]);

  switch (locale) {
    case "ko":
      return { locale: koKR };
    case "ja":
      return { locale: jaJP };
    case "th":
      return { locale: thTH };
    case "vi":
      return { locale: viVN };
    case "en":
    default:
      return { locale: enUS };
  }
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Retry up to 3 times per defensive API guidelines — transient network
      // failures and 502/503 responses are common with backend proxies.
      retry: 3,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

const initialRelayEnvironment = createRelayEnvironment();

const App = () => {
  const { locale } = useLocale();
  const { resolvedColorScheme } = useColorScheme();
  const isDark = resolvedColorScheme === "dark";

  const [relayEnvironment, setRelayEnvironment] = useState(
    initialRelayEnvironment
  );

  // Dismiss the boot splash (index.html) now that React has mounted. The
  // splash's own timer is a fallback if this never runs. Dismissal is
  // idempotent, so Strict Mode's dev-only double effect run and the late
  // fallback timer are both harmless no-ops.
  useEffect(() => {
    window.__dismissBootSplash?.();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BAIConfigProvider
        locale={locale}
        theme={{
          algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
          token: {
            colorPrimary: isDark ? "#4096FF" : "#1677FF",
            colorSuccess: "#37B076",
            ...(isDark ? {} : { colorBorder: "#DEE1E7" }),
            fontFamily: "Ubuntu",
            fontFamilyCode: "Ubuntu Mono",
          },
          components: {
            Button: {
              primaryShadow: "none",
            },
            Table: {
              headerBg: isDark ? "#292929" : "#E3E3E3",
              headerSplitColor: isDark
                ? "rgba(255,255,255,0.15)"
                : "rgba(0,0,0,0.25)",
            },
            Tag: {
              borderRadiusSM: 1,
            },
            Collapse: {
              colorFillAlter: isDark ? "#1f1f1f" : "#FAFAFA",
            },
            Result: {
              titleFontSize: 20,
              subtitleFontSize: 14,
            },
          },
        }}
      >
        <I18nextProvider i18n={i18n}>
          <AntdApp>
            <QueryErrorHandler />
            <CurrentUserErrorToaster />
            <OutdatedBrowserAlert />
            <RelayEnvironmentProvider environment={relayEnvironment}>
              <Suspense
                fallback={
                  <FlexActivityIndicator
                    style={{ height: "100vh" }}
                    size={48}
                  />
                }
              >
                <AuthProvider
                  onLogin={() => {}}
                  onLogout={() => {
                    setRelayEnvironment(createRelayEnvironment());
                  }}
                  onInvalidToken={() => {
                    setRelayEnvironment(createRelayEnvironment());
                  }}
                >
                  <Routes>
                    <Route
                      path="/"
                      element={
                        <RequireAuth>
                          <MainLayout />
                        </RequireAuth>
                      }
                    >
                      <Route
                        path="/"
                        element={<Navigate to="/dashboard" replace />}
                      />
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route
                        path="/data/*"
                        element={
                          <RequireFeature
                            flag="data_folders"
                            fallback={<NoMatch />}
                          >
                            <DataFoldersPage />
                          </RequireFeature>
                        }
                      />
                      <Route path="/pipelines/*" element={<PipelineList />} />
                      <Route
                        path="/pipelines/:pipelineId"
                        element={<PipelineList />}
                      />
                      <Route
                        path="/pipeline-jobs/*"
                        element={<PipelineJobsPage />}
                      />
                      <Route
                        path="pipeline-jobs/:jobId"
                        element={<PipelineJobDetail />}
                      />
                      <Route
                        path="/deployments/*"
                        element={
                          <RequireFeature
                            flag="model_deployments"
                            fallback={<NoMatch />}
                          >
                            <ModelDeploymentsPage />
                          </RequireFeature>
                        }
                      />
                      <Route path="/prompts/*" element={<PromptsPage />} />
                      <Route
                        path="/agents"
                        element={
                          <RequireFeature
                            flag="agent_management"
                            fallback={<NoMatch />}
                          >
                            <AgentsPage />
                          </RequireFeature>
                        }
                      />
                      <Route
                        path="/agents/:agentId"
                        element={
                          <RequireFeature
                            flag="agent_management"
                            fallback={<NoMatch />}
                          >
                            <AgentDetailPage />
                          </RequireFeature>
                        }
                      />
                      <Route
                        path="/agents/:agentId/sessions/:sessionId"
                        element={
                          <RequireFeature
                            flag="agent_management"
                            fallback={<NoMatch />}
                          >
                            <AgentChatPage />
                          </RequireFeature>
                        }
                      />
                      <Route
                        path="/secrets/*"
                        element={
                          <RequireFeature flag="secrets" fallback={<NoMatch />}>
                            <SecretsPage />
                          </RequireFeature>
                        }
                      />
                      <Route
                        path="/experiments"
                        element={
                          <RequireFeature
                            flag="experiments"
                            fallback={<NoMatch />}
                          >
                            <ExperimentsPage />
                          </RequireFeature>
                        }
                      />
                      <Route
                        path="/experiments/:experimentId"
                        element={
                          <RequireFeature
                            flag="experiments"
                            fallback={<NoMatch />}
                          >
                            <ExperimentDetailPage />
                          </RequireFeature>
                        }
                      />
                      <Route
                        path="/pipeline-templates"
                        element={
                          <RequireFeature
                            flag="pipeline_templates"
                            fallback={<NoMatch />}
                          >
                            <PipelineTemplatesPage />
                          </RequireFeature>
                        }
                      />
                      <Route
                        path="/audit-logs/*"
                        element={
                          <RequirePrivilege
                            privileges={["superadmin", "admin"]}
                            fallback={<AccessDenied />}
                          >
                            <AuditLogsPage />
                          </RequirePrivilege>
                        }
                      />
                      <Route path="/settings/*" element={<SettingsPage />} />
                    </Route>
                    <Route
                      path="login"
                      element={
                        <RedirectIfAuth>
                          <LoginPage />
                        </RedirectIfAuth>
                      }
                    />
                    <Route path="*" element={<NoMatch />} />
                  </Routes>
                </AuthProvider>
              </Suspense>
            </RelayEnvironmentProvider>
          </AntdApp>
        </I18nextProvider>
      </BAIConfigProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

export default App;
